import axios from "axios";
import { toast } from "react-toastify";
import authStore from "./auth.store";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Request timeout in ms (default 2 min). Short timeouts can cause false "session expired" when server is slow.
const REQUEST_TIMEOUT_MS = process.env.REACT_APP_REQUEST_TIMEOUT
  ? parseInt(process.env.REACT_APP_REQUEST_TIMEOUT, 10)
  : 2 * 60 * 1000;

// Longer timeout for batch report and other heavy endpoints (default 10 min). Override with REACT_APP_BATCH_REPORT_TIMEOUT.
export const LONG_REQUEST_TIMEOUT_MS = process.env.REACT_APP_BATCH_REPORT_TIMEOUT
  ? parseInt(process.env.REACT_APP_BATCH_REPORT_TIMEOUT, 10)
  : 10 * 60 * 1000;

const httpClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-type": "application/json",
  },
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
  timeout: REQUEST_TIMEOUT_MS,
});

// Backend should never return PII or stack traces in error.message / userMessage; use generic messages in production.
const buildUserMessage = (error, correlationId) => {
  if (!error) return "Something went wrong while processing your request.";
  const parts = [error.whatHappened, error.endResult, error.prevention].filter(Boolean);
  let message = parts.join(" ");
  if (!message) {
    message = "Something went wrong while processing your request.";
  }
  if (correlationId) {
    message = `${message} Reference: ${correlationId}.`;
  }
  return message;
};

const normalizeErrorPayload = (payload) => {
  if (!payload) return payload;
  if (payload.success !== false) return payload;
  const correlationId = payload.correlationId;
  const userMessage = buildUserMessage(payload.error, correlationId);
  return {
    ...payload,
    userMessage,
    message: payload.message || userMessage,
    err: payload.err || { message: userMessage },
  };
};

httpClient.interceptors.request.use(
  function (config) {
    // Ensure headers object exists
    if (!config.headers) {
      config.headers = {};
    }

    // Get auth token from store (compatible with older Babel)
    const state = authStore.getState();
    const authToken = state && state.auth_reducer && state.auth_reducer.authToken;

    // Set token if it exists
    if (authToken) {
      config.headers["x-auth-token"] = authToken;
    }

    return config;
  },
  function (error) {
    if (process.env.NODE_ENV === "development") console.error("axios.interceptors.request error => ", error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 errors (token expiration)
httpClient.interceptors.response.use(
  function (response) {
    if (response && response.data && response.data.success === false) {
      response.data = normalizeErrorPayload(response.data);
    }
    // Pass through successful responses
    return response;
  },
  function (error) {
    if (error && error.response && error.response.data) {
      error.response.data = normalizeErrorPayload(error.response.data);
      error.userMessage =
        error.response &&
        error.response.data &&
        error.response.data.userMessage;
    }
    if (!error.userMessage) {
      // Network/offline: show a clear message instead of generic axios error
      error.userMessage = (!error.response && (error.message === "Network Error" || error.code === "ECONNABORTED"))
        ? "Request failed. Check your connection and try again."
        : (error.message || "Something went wrong while processing your request.");
    }
    // Show API errors as toast (standardized display); skip when caller handles (e.g. login form)
    const skipToast = error.config && error.config.skipErrorToast;
    if (!skipToast && (!error.response || error.response.status !== 401)) {
      toast(error.userMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        type: "error",
      });
    }
    // Handle 401 Unauthorized only (expired or invalid token). Do NOT redirect on request timeout (ECONNABORTED) or network errors.
    const isTimeoutOrNetwork = !error.response && (error.code === "ECONNABORTED" || error.message === "Network Error");
    if (!isTimeoutOrNetwork && error.response && error.response.status === 401) {
      // Import auth store actions
      const authStoreModule = require("./auth.store");
      const types = authStoreModule.types;
      const actions = authStoreModule.actions;

      // Clear auth storage and reset state
      actions.removeAuthStorage();
      authStore.dispatch({ type: types.INIT_AUTH_STATE });

      // Redirect to login page
      const subdomain = process.env.REACT_APP_SUB_DOMAIN || '';
      window.location.href = subdomain + '/login';
    }

    return Promise.reject(error);
  }
);

export default httpClient;
